import {
    Fragment,
    ReactNode,
    isValidElement,
    forwardRef,
    LegacyRef,
    cloneElement,
    Children,
} from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
    ChevronDownIcon,
    EllipsisVerticalIcon,
} from '@heroicons/react/20/solid';
import { cn } from '../../utils';
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useInteractions,
    FloatingPortal,
    Placement,
} from '@floating-ui/react';

type DropdownProps = {
    trigger?: ReactNode;
    trailingIcon?: boolean;
    children?: ReactNode;
    zIndex?: number;
    placement?: Placement;
} & EllipsisOrientation;

export default function Dropdown({
    trigger,
    trailingIcon = false,
    ellipsisOrientation = 'vertical',
    children,
    zIndex = 99999,
    placement = 'bottom-start',
    ...rest
}: DropdownProps) {
    return (
        <Menu
            {...rest}
            as="div"
            className="alli-relative alli-inline-block alli-text-left">
            {({ open }) => {
                const { refs, floatingStyles } = useFloating({
                    placement: placement,
                    open: open,
                    middleware: [
                        offset({
                            mainAxis: 8,
                        }),
                        flip(),
                        shift(),
                    ],
                    whileElementsMounted: autoUpdate,
                });

                // Merge all the interactions into prop getters
                const { getReferenceProps, getFloatingProps } =
                    useInteractions();

                return (
                    <>
                        <Trigger
                            trigger={trigger}
                            ellipsisOrientation={ellipsisOrientation}
                            ref={refs.setReference}
                            {...getReferenceProps()}
                        />

                        {open && (
                            <FloatingPortal>
                                <Transition
                                    as={Fragment}
                                    enter="alli-transition alli-ease-out alli-duration-100"
                                    enterFrom="alli-transform alli-opacity-0 alli-scale-95"
                                    enterTo="alli-transform alli-opacity-100 alli-scale-100"
                                    leave="alli-transition alli-ease-in alli-duration-75"
                                    leaveFrom="alli-transform alli-opacity-100 alli-scale-100"
                                    leaveTo="alli-transform alli-opacity-0 alli-scale-95">
                                    <Menu.Items
                                        className="alli-absolute alli-right-0 alli-w-56 alli-origin-top-right alli-overflow-hidden alli-rounded-md alli-bg-white alli-shadow-lg alli-ring-1 alli-ring-black alli-ring-opacity-5 focus:alli-outline-none"
                                        ref={refs.setFloating}
                                        style={{ ...floatingStyles, zIndex }}
                                        {...getFloatingProps()}>
                                        {children &&
                                            Children.toArray(children).map(
                                                (child: JSX.Element) => {
                                                    if (
                                                        child?.type
                                                            .displayName ===
                                                        'MenuItem'
                                                    ) {
                                                        return cloneElement(
                                                            child,
                                                            {
                                                                trailingIcon,
                                                            },
                                                        );
                                                    }
                                                    return child;
                                                },
                                            )}
                                    </Menu.Items>
                                </Transition>
                            </FloatingPortal>
                        )}
                    </>
                );
            }}
        </Menu>
    );
}

type TriggerProps = {
    trigger?: ReactNode;
} & EllipsisOrientation;

const Trigger = forwardRef(function Trigger(
    { trigger, ellipsisOrientation }: TriggerProps,
    ref: LegacyRef<HTMLDivElement>,
) {
    return (
        <div ref={ref}>
            {trigger && isValidElement(trigger) ? (
                <Menu.Button className="alli-rounded-full">
                    <span className="alli-sr-only">Open options</span>
                    {trigger}
                </Menu.Button>
            ) : typeof trigger === 'string' ? (
                <Menu.Button className="alli-inline-flex alli-w-full alli-justify-center alli-gap-x-1.5 alli-rounded-md alli-bg-white alli-px-3 alli-py-2 alli-text-sm alli-font-medium alli-text-blue-gray-700 alli-shadow-sm alli-ring-1 alli-ring-inset alli-ring-gray-400">
                    <span className="alli-sr-only">Open options</span>
                    {trigger}
                    <ChevronDownIcon
                        className="-alli-mr-1 alli-h-5 alli-w-5 alli-stroke-[1.5px] alli-text-gray-700"
                        aria-hidden="true"
                    />
                </Menu.Button>
            ) : (
                <EllipsisButton ellipsisOrientation={ellipsisOrientation} />
            )}
        </div>
    );
});

type EllipsisOrientation = {
    ellipsisOrientation?: 'vertical' | 'horizontal';
};

function EllipsisButton({
    ellipsisOrientation = 'vertical',
}: EllipsisOrientation) {
    return (
        <Menu.Button
            className={cn(
                'alli-flex alli-transform alli-items-center alli-rounded-full alli-bg-white alli-text-gray-750 ',
                'hover:alli-bg-gray-300 hover:alli-text-blue-gray-700',
                'focus:alli-outline-none focus:alli-ring-2 focus:alli-ring-blue-500 focus:alli-ring-offset-2 focus:alli-ring-offset-gray-100 focus-visible:alli-bg-gray-300 focus-visible:alli-text-blue-gray-800',
                {
                    'alli-rotate-0': ellipsisOrientation === 'vertical',
                    'alli-rotate-90': ellipsisOrientation !== 'vertical',
                },
            )}>
            <span className="alli-sr-only">Open options</span>
            <EllipsisVerticalIcon
                data-testid="ellipsis-icon"
                className="alli-h-5 alli-w-5"
                aria-hidden="true"
            />
        </Menu.Button>
    );
}
